import { useState, useEffect, useRef, useMemo } from 'react';
import { useUser } from '../../context/UserContext';
import { useChat } from './ChatContext';
import { useTeamUsers } from './useTeamUsers';
import type { Database } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';
import { Loader2 } from 'lucide-react';
import { Avatar, Badge, Button, Checkbox, Icon, Input, Popover, SidePanel } from '../ui';

interface Client {
  id: string;
  name: string;
}

interface CreateChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateChannelModal({ isOpen, onClose }: CreateChannelModalProps) {
  const { user } = useUser();
  const { triggerChannelRefresh, setSelectedChannelId } = useChat();
  const { users: teamUsers, loading: loadingUsers } = useTeamUsers();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'general' | 'client'>('general');
  const [clientId, setClientId] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const nameFieldRef = useRef<HTMLDivElement>(null);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  // Focus name input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => nameFieldRef.current?.querySelector('input')?.focus(), 100);
    }
  }, [isOpen]);

  // Reset form on close
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
      setType('general');
      setClientId(null);
      setSelectedMembers(new Set());
      setError(null);
      setClientSearch('');
      setShowClientDropdown(false);
    }
  }, [isOpen]);

  // Fetch clients when type is 'client'
  useEffect(() => {
    if (type !== 'client' || !user?.agency_id) {
      setClients([]);
      return;
    }

    const fetchClients = async () => {
      setLoadingClients(true);
      const { data, error: fetchError } = await supabase
        .from('clients')
        .select('id, name')
        .eq('agency_id', user.agency_id!)
        .order('name');

      if (!fetchError && data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setClients(data.map((c: any) => ({ id: c.id, name: c.name })));
      }
      setLoadingClients(false);
    };

    fetchClients();
  }, [type, user?.agency_id]);

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const q = clientSearch.toLowerCase();
    return clients.filter(c => c.name.toLowerCase().includes(q));
  }, [clients, clientSearch]);

  const selectedClient = useMemo(
    () => clients.find(c => c.id === clientId),
    [clients, clientId]
  );

  const toggleMember = (userId: string) => {
    setSelectedMembers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const selectAllMembers = () => {
    setSelectedMembers(new Set(teamUsers.map(u => u.id)));
  };

  const deselectAllMembers = () => {
    setSelectedMembers(new Set());
  };

  const handleCreate = async () => {
    if (!user || !name.trim()) return;

    setCreating(true);
    setError(null);

    try {
      // Insert channel
      if (!user.agency_id) throw new Error('Your account has no agency');
      const channelData: Database['public']['Tables']['chat_channels']['Insert'] = {
        name: name.trim(),
        type,
        agency_id: user.agency_id,
        created_by: user.id,
        archived: false,
      };

      if (description.trim()) {
        channelData.description = description.trim();
      }

      if (type === 'client' && clientId) {
        channelData.client_id = clientId;
      }

      const { data: newChannel, error: channelError } = await supabase
        .from('chat_channels')
        .insert(channelData)
        .select()
        .single();

      if (channelError || !newChannel) {
        throw new Error(channelError?.message || 'Failed to create channel');
      }

      // Add creator as member + selected members
      const memberIds = new Set(selectedMembers);
      memberIds.add(user.id); // Always add creator

      const memberInserts = Array.from(memberIds).map(userId => ({
        channel_id: newChannel.id,
        user_id: userId,
      }));

      const { error: memberError } = await supabase
        .from('chat_channel_members')
        .insert(memberInserts);

      if (memberError) {
        console.error('Error adding channel members:', memberError);
        // Channel was created, just log the member error
      }

      triggerChannelRefresh();
      setTimeout(() => {
        setSelectedChannelId(newChannel.id);
      }, 100);

      onClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.message || 'Failed to create channel');
    } finally {
      setCreating(false);
    }
  };

  const typeButtonStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    height: 'var(--control-md)',
    borderRadius: 'var(--radius-sm)',
    border: active ? '1px solid transparent' : '1px solid var(--border-hairline)',
    background: active ? 'var(--accent)' : 'var(--surface-raised)',
    color: active ? 'var(--text-on-accent)' : 'var(--text-muted)',
    fontFamily: 'var(--font-ui)',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
    cursor: 'pointer',
    transition: 'background var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
  });

  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      eyebrow="Chat"
      title="New Channel"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={creating} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || creating}
            style={{ flex: 1 }}
          >
            {creating ? 'Creating...' : 'Create Channel'}
          </Button>
        </>
      }
    >
      <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {/* Channel Name */}
        <div ref={nameFieldRef}>
          <Input
            label="Channel Name *"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. marketing, design-team"
          />
        </div>

        {/* Description */}
        <Input
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="What's this channel about?"
        />

        {/* Type */}
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Type</div>
          <div className="flex gap-2">
            <button
              type="button"
              className="pressable"
              onClick={() => {
                setType('general');
                setClientId(null);
              }}
              style={typeButtonStyle(type === 'general')}
            >
              General
            </button>
            <button
              type="button"
              className="pressable"
              onClick={() => setType('client')}
              style={typeButtonStyle(type === 'client')}
            >
              Client
            </button>
          </div>
        </div>

        {/* Client Picker */}
        {type === 'client' && (
          <div ref={clientDropdownRef} className="relative">
            <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Client</div>
            <button
              type="button"
              onClick={() => setShowClientDropdown(prev => !prev)}
              className="pressable w-full flex items-center justify-between"
              style={{
                height: 'var(--control-md)',
                padding: '0 12px',
                background: 'var(--surface-input)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-base)',
                color: selectedClient ? 'var(--text-body)' : 'var(--text-faint)',
                cursor: 'pointer',
                transition: 'border-color var(--dur-base) var(--ease-out)',
              }}
            >
              <span>{selectedClient ? selectedClient.name : 'Select a client...'}</span>
              <Icon name="chevron-down" size={14} style={{ color: 'var(--text-faint)' }} />
            </button>

            <Popover
              open={showClientDropdown}
              anchorEl={clientDropdownRef.current}
              onClose={() => setShowClientDropdown(false)}
              matchWidth
              role="listbox"
              style={{ padding: 0, overflow: 'hidden' }}
            >
                <div
                  className="flex items-center gap-1.5 px-3 py-2"
                  style={{ borderBottom: '1px solid var(--border-hairline)' }}
                >
                  <Icon name="search" size={13} style={{ color: 'var(--text-faint)' }} />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    placeholder="Search clients..."
                    className="flex-1 bg-transparent outline-none"
                    style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-body)',
                    }}
                    autoFocus
                  />
                </div>
                <div className="max-h-[180px] overflow-y-auto chat-scrollbar" style={{ padding: '4px' }}>
                  {loadingClients ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-faint)' }} />
                    </div>
                  ) : filteredClients.length === 0 ? (
                    <p
                      className="text-center py-3"
                      style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
                    >
                      No clients found
                    </p>
                  ) : (
                    filteredClients.map(c => (
                      <button
                        key={c.id}
                        onClick={() => {
                          setClientId(c.id);
                          setShowClientDropdown(false);
                          setClientSearch('');
                        }}
                        className="hover-wash pressable w-full flex items-center justify-between text-left"
                        style={{
                          padding: '7px 10px',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          background: c.id === clientId ? 'var(--surface-active)' : 'transparent',
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--text-body)',
                          cursor: 'pointer',
                        }}
                      >
                        <span>{c.name}</span>
                        {c.id === clientId && (
                          <Icon name="check" size={13} style={{ color: 'var(--accent-text)' }} />
                        )}
                      </button>
                    ))
                  )}
                </div>
            </Popover>
          </div>
        )}

        {/* Members */}
        <div>
          <div className="flex items-center justify-between" style={{ marginBottom: '6px' }}>
            <span className="t-eyebrow">Members</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={selectAllMembers}
                className="hover-underline pressable"
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                  color: 'var(--accent-text)',
                  cursor: 'pointer',
                }}
              >
                Select all
              </button>
              {selectedMembers.size > 0 && (
                <button
                  type="button"
                  onClick={deselectAllMembers}
                  className="hover-underline pressable"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div
            className="overflow-hidden"
            style={{
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            {loadingUsers ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-faint)' }} />
              </div>
            ) : teamUsers.length === 0 ? (
              <p
                className="text-center py-3"
                style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
              >
                No team members found
              </p>
            ) : (
              <div className="max-h-[200px] overflow-y-auto chat-scrollbar">
                {teamUsers.map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleMember(u.id)}
                    className="hover-wash pressable w-full flex items-center text-left"
                    style={{
                      gap: '10px',
                      padding: '8px 12px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    {/* Checkbox */}
                    <span style={{ pointerEvents: 'none' }}>
                      <Checkbox checked={selectedMembers.has(u.id)} />
                    </span>

                    {/* Avatar */}
                    <Avatar name={u.name} src={u.avatar} size={24} />

                    {/* Name */}
                    <span
                      className="truncate"
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                        color: 'var(--text-body)',
                      }}
                    >
                      {u.name}
                    </span>

                    {/* Role badge */}
                    {u.role && (
                      <span className="ml-auto flex-shrink-0">
                        <Badge tone="idle">{u.role}</Badge>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedMembers.size > 0 && (
            <p
              className="t-num"
              style={{ marginTop: '6px', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}
            >
              {selectedMembers.size} member{selectedMembers.size !== 1 ? 's' : ''} selected (+ you)
            </p>
          )}
        </div>

        {/* Error */}
        {error && (
          <p
            style={{
              margin: 0,
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-xs)',
            }}
          >
            {error}
          </p>
        )}
      </div>
    </SidePanel>
  );
}
